import {
  AddCircle,
  BorderColor,
  Search,
  Visibility,
} from "@mui/icons-material";
import { Box, Grid, TextField, Typography } from "@mui/material";
import useApi from "_api/index";
import Actions from "components/Car/Actions";
import TableData from "components/Car/Table";
import {
  GradientButton,
  GradientButtonOutline,
} from "components/GradientButton";
import ModalDetailCar from "components/Modal/Car/ModalDetailCar";
import CustomPagination from "components/TableExplanded/Pagination";
import React, { useEffect } from "react";
import { formatCurrency2Digit, formatDateTime } from "utils/helpers/format";
import {
  handlePageChange,
  handleRowsPerPageChange,
} from "utils/helpers/handlePage";

const SoldCars = () => {
  const [loading, setLoading] = React.useState(false);
  const [soldCars, setSoldCars] = React.useState([]);
  const [pagination, setPagination] = React.useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [filter, setFilter] = React.useState({
    search: "",
  });
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);

  const fetchData = async (isClear = false) => {
    setLoading(true);
    try {
      const response = await useApi.sold_car.getSoldCarList({
        page: pagination.page,
        limit: pagination.limit,
        ...(!isClear && filter),
      });
      if (response.success) {
        const { data, meta } = response;
        let list = [];
        data.map((car) => {
          list.push({
            ...car,
            ...car.customer,
          });
        });
        setSoldCars(list ?? []);
        setPagination({
          page: meta.page || 1,
          limit: meta.limit || 10,
          total: meta.total || 0,
          totalPages: meta.totalPages || 1,
        });
      }
    } catch (error) {
      console.error("Error fetching sold cars:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination.page, pagination.limit]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography component={"div"} sx={{ fontSize: "32px" }}>
          รถขายแล้ว
        </Typography>
        <GradientButtonOutline
          onClick={() => {
            // Handle the click event for adding a new car
          }}
          sx={{
            px: 3,
            display: "flex",
            alignItems: "center",
            height: "48px",
            backgroundColor: "white",
          }}
        >
          <Typography style={{ marginRight: "8px" }}>ส่งออก Excel</Typography>
        </GradientButtonOutline>
      </Box>

      <Grid
        container
        spacing={2}
        alignContent="flex-end"
        justifyContent="flex-end"
      >
        <Grid item xs={12} sm={6} md={4}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item xs={4} textAlign="right">
              <Typography
                sx={{
                  fontSize: "16px",
                  whiteSpace: "nowrap",
                }}
              >
                ค้นหาทั้งหมด
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="ค้นหาทั้งหมด"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "white",
                    height: "40px",
                  },
                }}
                onChange={(e) =>
                  setFilter((prev) => ({ ...prev, search: e.target.value }))
                }
                value={filter.search}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Box
            sx={{
              display: "flex",
              direction: "row",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <GradientButtonOutline
              sx={{
                px: 3,
                display: "flex",
                alignItems: "center",
                height: "40px",
              }}
              onClick={() => {
                setFilter({ search: "" });
                setPagination((prev) => ({ ...prev, page: 1 }));
                fetchData(true);
              }}
            >
              ล้าง
            </GradientButtonOutline>
            <GradientButton
              sx={{
                px: 3,
                display: "flex",
                alignItems: "center",
                height: "40px",
              }}
              endIcon={<Search />}
              onClick={() => {
                fetchData();
              }}
            >
              ค้นหา
            </GradientButton>
          </Box>
        </Grid>
      </Grid>

      {/* table data */}
      <TableData
        loading={false}
        data={soldCars}
        columns={[
          { key: "expand", label: "" },
          {
            key: "car_pictures",
            label: "รูปภาพ",
          },
          { key: "car_code", label: "รหัสรถ" },
          { key: "license_plate", label: "ทะเบียน" },
          { key: "car_brand", label: "ยี่ห้อ" },
          { key: "cost_price", label: "ราคาทุน" },
          {
            key: "resale_product_percentage",
            label: "ราคาขาย",
            render: (row) =>
              formatCurrency2Digit(row.resale_product_percentage),
          },
          {
            key: "sell_date",
            label: "วันที่ขาย",
            render: (row) => formatDateTime(row.sell_date),
          },
          {
            key: "market_channel",
            label: "ช่องทางการขาย",
          },
          {
            key: "payment_channel",
            label: "ช่องทางการชำระ",
          },
          {
            key: "action",
            label: "Actions",
            style: {
              width: "150px",
              textAlign: "center",
            },
            render: (row) => (
              <Actions
                menuList={[
                  {
                    label: "ดูรายละเอียด",
                    icon: <Visibility />,
                    onClick: () => {
                      // Handle view details action
                      setSelectedRow(row);
                      setIsModalOpen(true);
                    },
                  },
                ]}
              />
            ),
          },
        ]}
      />

      <CustomPagination
        totalPage={pagination.totalPages}
        page={pagination.page - 1}
        rowsPerPage={pagination.limit}
        onPageChange={(event, newPage) => {
          handlePageChange(event, newPage, setPagination);
        }}
        onRowsPerPageChange={(event) => {
          handleRowsPerPageChange(event, setPagination);
        }}
      />

      <ModalDetailCar
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        rowID={selectedRow?.car_id}
        type="sold_car"
        formlist={[
          {
            id: 1,
            document: "แบบคำขอโอนและรับโอน",
            document_type: "car_transform",
          },
          {
            id: 2,
            document: "หนังสือมอบอำนาจ",
            document_type: "car_power_of_attorney",
          },
          {
            id: 3,
            document: "หนังสือสัญญาซื้อ - ขายรถ",
            document_type: "car_sell_contact",
          },
        ]}
      />
    </Box>
  );
};
export default SoldCars;
